/**
 * FILE-OVERVIEW:
 * - 役割: カレンダー画面のUI状態（選択・モーダル・ビュー）を一元管理。
 * - 主な連携: components/Calendar/*
 * - 本番運用上の注意: 初期値変更は作成ダイアログのデフォルト値に影響する。
 */

import { useState, useRef, useMemo, useEffect } from 'react';
import { categoryColors, areaColors } from '../utils/colors';
import { getBranchBracketLabel } from '../utils/branchGroupLabel';

const allCategories = Object.keys(categoryColors);
const allAreas = Object.keys(areaColors);

const getDefaultAreaForGroups = (groups) => {
  const normalized = (Array.isArray(groups) ? groups : [])
    .map((group) => String(group || '').trim().toLowerCase())
    .filter(Boolean);

  if (normalized.includes('branch_aomori')) {
    return '青森';
  }

  if (normalized.some((group) => group.startsWith('branch_'))) {
    return '総支局';
  }

  return '本社';
};

/**
 * カレンダー画面のUI状態を集約するカスタムフック。
 *
 * @param {Object|null} currentUser ログインユーザー情報（新規イベント初期値に利用）
 * @returns {Object} モーダル開閉、選択状態、Drawer状態、カレンダー参照などの状態/操作群
 *
 * 本番運用上の注意:
 * - localStorageキー（root_* / shared_*）は既存ユーザー状態と互換性がある名前を維持する。
 */
export const useCalendarState = (currentUser) => {
  const [open, setOpen] = useState(false);
  const [tempEvent, setTempEvent] = useState({});
  const [dialogMode, setDialogMode] = useState('schedule');
  const [currentView, setCurrentView] = useState('dayGridMonth');
  // 自動更新（両画面共通でローカルストレージ管理）
  const [autoUpdate, setAutoUpdateState] = useState(() => {
    try { return JSON.parse(localStorage.getItem('shared_autoUpdate')) || false; } catch { return false; }
  });
  const setAutoUpdate = (value) => {
    setAutoUpdateState(value);
    localStorage.setItem('shared_autoUpdate', JSON.stringify(value));
  };
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // OFFにしたものをローカルストレージで管理（初期は全ON）
  const [disabledCategories, setDisabledCategories] = useState(() => {
    try { return JSON.parse(localStorage.getItem('root_disabledCategories')) || []; } catch { return []; }
  });
  const [disabledAreas, setDisabledAreas] = useState(() => {
    try { return JSON.parse(localStorage.getItem('root_disabledAreas')) || []; } catch { return []; }
  });

  const [showOnlyMine, setShowOnlyMineState] = useState(() => {
    try { return JSON.parse(localStorage.getItem('root_showOnlyMine')) || false; } catch { return false; }
  });
  const setShowOnlyMine = (value) => {
    setShowOnlyMineState(value);
    localStorage.setItem('root_showOnlyMine', JSON.stringify(value));
  };

  const selectedCategories = useMemo(() => allCategories.filter(v => !disabledCategories.includes(v)), [disabledCategories]);
  const selectedAreas = useMemo(() => allAreas.filter(v => !disabledAreas.includes(v)), [disabledAreas]);

  const setSelectedCategories = (selected) => {
    const disabled = allCategories.filter(v => !selected.includes(v));
    setDisabledCategories(disabled);
    localStorage.setItem('root_disabledCategories', JSON.stringify(disabled));
  };
  const setSelectedAreas = (selected) => {
    const disabled = allAreas.filter(v => !selected.includes(v));
    setDisabledAreas(disabled);
    localStorage.setItem('root_disabledAreas', JSON.stringify(disabled));
  };

  useEffect(() => {
    const normalizedGroups = (Array.isArray(currentUser?.groups) ? currentUser.groups : [])
      .map((group) => String(group || '').trim().toLowerCase())
      .filter(Boolean);

    if (normalizedGroups.length === 0) return;

    const selected = normalizedGroups.includes('branch_aomori')
      ? ['青森']
      : [...allAreas];
    const disabled = allAreas.filter((area) => !selected.includes(area));

    setDisabledAreas(disabled);
    localStorage.setItem('root_disabledAreas', JSON.stringify(disabled));
  }, [currentUser?.groups]);
  
  const calendarRef = useRef(null);

  const toggleDrawer = (open) => () => {
    setIsDrawerOpen(open);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const setDefaultEvent = (dateStr) => {
    const defaultStart = dateStr;
    const defaultEnd = dateStr;
    const basePerson = (currentUser?.name || currentUser?.username || '').trim();
    const branchBracketLabel = getBranchBracketLabel(currentUser?.groups);
    const defaultArea = getDefaultAreaForGroups(currentUser?.groups);
    const personWithBranch = branchBracketLabel && basePerson && !basePerson.startsWith(branchBracketLabel)
      ? `${branchBracketLabel}${basePerson}`
      : basePerson;

    setTempEvent({
      date: dateStr,
      notes: '',
      title: '',
      start: defaultStart,
      end: defaultEnd,
      startTime: '未定',
      endTime: '未定',
      category: '',
      createdBy: currentUser?.userId || '',
      createdByUsername: currentUser?.username || '',
      person: personWithBranch,
      area: defaultArea
    });
  };

  return {
    // State
    open,
    setOpen,
    tempEvent,
    setTempEvent,
    dialogMode,
    setDialogMode,
    currentView,
    setCurrentView,
    autoUpdate,
    setAutoUpdate,
    isDrawerOpen,
    setIsDrawerOpen,
    isSaving,
    setIsSaving,
    selectedCategories,
    setSelectedCategories,
    selectedAreas,
    setSelectedAreas,
    showOnlyMine,
    setShowOnlyMine,
    calendarRef,
    
    // Methods
    toggleDrawer,
    handleClose,
    setDefaultEvent
  };
};